import React, { useEffect } from "react";
import { Controller } from "react-hook-form";
import EditEscrowModal from "../../popups/EditEscrowModal";
import ControlledField from "./ControlledField";
import { calculateDiscount, computeTitleFeePayorAmount } from "../utils/calculations";
import { applyWaSalesTaxClosingCosts as mergeWaSalesTaxClosingCosts } from "../utils/waSalesTaxClosingCosts";

export default function EscrowTitleFields({
  mainId,
  control,
  isInitialLoad,
  CustomSelect,
  openModal,
  closeModal,
  FloatingNumber,
  cleanNumber,
  handleCurrencyBlur,
  setValue,
  setFormData,
  onFormatted,
  EscrowDisabled,
  onContinue,
  formData,
}) {
  const titleMeta = formData?.titleSettlementMeta ?? {};

  const prevDiscountRef = React.useRef(titleMeta.discount);
  const prevDiscountAmountRef = React.useRef(titleMeta.discountAmount);

  useEffect(() => {
    if (isInitialLoad || !formData?.titleSettlementMeta) return;
    let isDiscountChanged = true;
    if (prevDiscountRef.current !== titleMeta.discount) {
      isDiscountChanged = true;
    } else if (prevDiscountAmountRef.current !== titleMeta.discountAmount) {
      isDiscountChanged = false;
    }
    const getDiscountdata = calculateDiscount(formData, isDiscountChanged);
    const nextDiscount = getDiscountdata.discountPercent;
    const nextAmount = getDiscountdata.discountAmount;

    if (
      String(titleMeta.discount ?? "") === String(nextDiscount ?? "") &&
      String(titleMeta.discountAmount ?? "") === String(nextAmount ?? "")
    ) {
      prevDiscountRef.current = nextDiscount;
      prevDiscountAmountRef.current = nextAmount;
      return;
    }

    prevDiscountRef.current = nextDiscount;
    prevDiscountAmountRef.current = nextAmount;

    setFormData((prev) => ({
      ...prev,
      titleSettlementMeta: {
        ...prev.titleSettlementMeta,
        discount: nextDiscount,
        discountAmount: nextAmount,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleMeta.discount, titleMeta.discountAmount, isInitialLoad]);

  // Automatically sync payor + amount on mount and when payor changes
  useEffect(() => {
    if (isInitialLoad) return;
    const payor = titleMeta?.escrowSettlementPayor;
    if (!payor) return;

    const totalFee = parseFloat(titleMeta?.escrowSettlementBuyer || "0");
    const splitFee =
      titleMeta?.escrowSettlementSplit &&
      !isNaN(Number(titleMeta.escrowSettlementSplit))
        ? parseFloat(titleMeta.escrowSettlementSplit)
        : totalFee / 2;

    let selectedAmount = "0.00";

    switch (payor) {
      case "Split":
        selectedAmount = splitFee.toFixed(2);
        break;
      case "Buyer":
        selectedAmount = totalFee.toFixed(2);
        break;
      case "Seller":
        selectedAmount = "0.00";
        break;
      default:
        selectedAmount = totalFee.toFixed(2);
        break;
    }

    setFormData((prev) => ({
      ...prev,
      titleSettlementMeta: {
        ...(prev?.titleSettlementMeta ?? {}),
        escrowSettlementPayor: payor,
        escrowSettlementAmount: selectedAmount,
        escrowSettlementSplit: splitFee.toFixed(2),
      },
    }));

    setValue("titleSettlementMeta.escrowSettlementAmount", selectedAmount);
    setValue("titleSettlementMeta.escrowSettlementPayor", payor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleMeta?.escrowSettlementPayor, setValue]);

  useEffect(() => {
    if (isInitialLoad) return;
    const payor = titleMeta?.ownersTitlePayor;
    if (!payor) return;

    const totalFee = parseFloat(titleMeta?.ownersTitleBuyer || "0");
    const splitFee = totalFee / 2;

    let selectedAmount = "0.00";

    switch (payor) {
      case "Split":
        selectedAmount = splitFee.toFixed(2);
        break;
      case "Buyer":
        selectedAmount = totalFee.toFixed(2);
        break;
      case "Seller":
        selectedAmount = "0.00";
        break;
      default:
        selectedAmount = totalFee.toFixed(2);
        break;
    }

    setFormData((prev) => ({
      ...prev,
      titleSettlementMeta: {
        ...(prev?.titleSettlementMeta ?? {}),
        ownersTitlePayor: payor,
        ownersTitleAmount: selectedAmount,
        ownersTitleBuyer: totalFee.toFixed(2),
      },
    }));

    setValue("titleSettlementMeta.ownersTitleAmount", selectedAmount);
    setValue("titleSettlementMeta.ownersTitlePayor", payor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleMeta?.ownersTitlePayor, setValue]);

  useEffect(() => {
    if (isInitialLoad) return;
    const payor = titleMeta?.lendersTitlePayor;
    if (!payor) return;

    const isCash = formData?.loanInfoMeta?.buyersLoanType === "Cash";
    const totalFee = parseFloat(titleMeta?.lendersTitleBuyer || "0");
    const splitFee = totalFee / 2;

    let selectedAmount = "0.00";

    if (isCash) {
      selectedAmount = "0.00";
    } else {
      switch (payor) {
        case "Split":
          selectedAmount = splitFee.toFixed(2);
          break;
        case "Buyer":
          selectedAmount = totalFee.toFixed(2);
          break;
        case "Seller":
          selectedAmount = "0.00";
          break;
        default:
          selectedAmount = totalFee.toFixed(2);
          break;
      }
    }

    setFormData((prev) => ({
      ...prev,
      titleSettlementMeta: {
        ...(prev?.titleSettlementMeta ?? {}),
        lendersTitlePayor: payor,
        lendersTitleAmount: selectedAmount,
        lendersTitleBuyer: totalFee.toFixed(2),
      },
    }));

    setValue("titleSettlementMeta.lendersTitleAmount", selectedAmount);
    setValue("titleSettlementMeta.lendersTitlePayor", payor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    titleMeta?.lendersTitlePayor,
    formData?.loanInfoMeta?.buyersLoanType,
    setValue,
  ]);

  const syncClosingCostRowToRhf = (index, amount) => {
    if (typeof setValue !== "function") return;
    const n = Number(amount) || 0;
    const s = n.toFixed(2);
    const base = `closingCostsMeta.${index}`;
    setValue(`${base}.value`, s, { shouldDirty: true, shouldTouch: true });
    setValue(`${base}.total`, s, { shouldDirty: true, shouldTouch: true });
    setValue(`${base}.type`, "Flat Fee", {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const applyWaSalesTaxClosingCosts = (
    prev,
    { escrowSettlementAmount, lendersTitleAmount } = {},
  ) => {
    const state = prev?.addressMeta?.state;
    const salesTaxRate = Number(prev?.titleSettlementMeta?.salesTaxRate) || 0;
    const resolvedMainId = Number(mainId);

    if (resolvedMainId !== 5 || state !== "WA" || salesTaxRate === 0) {
      return prev;
    }

    const escrowAmount = Number(escrowSettlementAmount) || 0;
    const lenderAmount = Number(lendersTitleAmount) || 0;
    const taxLenderAmount = lenderAmount * salesTaxRate;
    const taxEscrowAmount = escrowAmount * salesTaxRate;

    const next = mergeWaSalesTaxClosingCosts(prev, {
      escrowSettlementAmount,
      lendersTitleAmount,
      salesTaxRate,
      lenderRowIndex: 11,
      escrowTaxRowIndex: 12,
    });

    syncClosingCostRowToRhf(11, taxLenderAmount);
    syncClosingCostRowToRhf(12, taxEscrowAmount);

    return next;
  };

  const parseAmount = (value) => {
    const normalized = String(value ?? "").replace(/,/g, "");
    return Number(normalized) || 0;
  };

  return (
    <>
      {/* Escrow / Settlement */}
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group">
          <label className="lbl-section">Escrow or Settlement</label>
          <Controller
            name="titleSettlementMeta.escrowSettlementPayor"
            control={control}
            defaultValue={titleMeta.escrowSettlementPayor ?? ""}
            render={({ field }) => (
              <CustomSelect
                {...field}
                placeholder={titleMeta.escrowSettlementPayor ?? "Select"}
                options={["Split", "Buyer", "Seller"]}
                onChange={(value) => {
                  field.onChange(value);

                  setFormData((prev) => {
                    const prevTitle = prev?.titleSettlementMeta ?? {};
                    let selectedAmount = "0.00";

                    switch (value) {
                      case "Split":
                        selectedAmount =
                          prevTitle.escrowSettlementSplit ?? "0.00";
                        break;
                      case "Buyer":
                        selectedAmount =
                          prevTitle.escrowSettlementBuyer ?? "0.00";
                        break;
                      case "Seller":
                        selectedAmount = "0.00";
                        break;
                      default:
                        selectedAmount =
                          prevTitle.escrowSettlementBuyer ?? "0.00";
                        break;
                    }

                    const updatedFormData = {
                      ...prev,
                      titleSettlementMeta: {
                        ...prevTitle,
                        escrowSettlementPayor: value,
                        escrowSettlementAmount: selectedAmount,
                      },
                    };

                    return applyWaSalesTaxClosingCosts(updatedFormData, {
                      escrowSettlementAmount: selectedAmount,
                      lendersTitleAmount:
                        updatedFormData?.titleSettlementMeta
                          ?.lendersTitleAmount,
                    });
                  });
                }}
              />
            )}
          />
        </div>
        <div className="calc-frm-fld form-group">
          <div className="calc-frm-field">
            <div className="calc-frm-fld form-group sign-of-amount no-m-space">
              <ControlledField
                name="titleSettlementMeta.escrowSettlementAmount"
                control={control}
                InputComponent={FloatingNumber}
                placeholder="0.00"
                type="text"
                defaultValue={titleMeta.escrowSettlementAmount ?? "0.00"}
                cleanNumber={cleanNumber}
                handleCurrencyBlur={handleCurrencyBlur}
                inputProps={{
                  disabled: EscrowDisabled.escrowSettlement,
                  className:
                    EscrowDisabled && EscrowDisabled.escrowSettlement === false
                      ? "premium-border"
                      : "",
                }}
                onFormatted={(fieldName, formatted) => {
                  onFormatted?.("escrowSettlementAmount", formatted);
                  setFormData((prev) => {
                    const prevTitle = prev?.titleSettlementMeta ?? {};
                    const updatedFormData = {
                      ...prev,
                      titleSettlementMeta: {
                        ...prevTitle,
                        escrowSettlementAmount: formatted,
                      },
                    };

                    return applyWaSalesTaxClosingCosts(updatedFormData, {
                      escrowSettlementAmount: parseAmount(formatted),
                      lendersTitleAmount: parseAmount(
                        updatedFormData?.titleSettlementMeta
                          ?.lendersTitleAmount,
                      ),
                    });
                  });
                }}
              />
            </div>
            <div className="calc-frm-fld form-group no-m-space edit-container">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  openModal({
                    title: "Edit Escrow / Settlement Fee",
                    size: "smallmodel",
                    content: (
                      <EditEscrowModal
                        description="Escrow Title Policy"
                        onClose={closeModal}
                        onContinue={(setting) =>
                          onContinue({ escrowSettlement: setting })
                        }
                      />
                    ),
                  });
                }}
              >
                <i className="icon-edit" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Owner’s Title Policy */}
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group">
          <label className="lbl-section">Owners Title Policy</label>
          <Controller
            name="titleSettlementMeta.ownersTitlePayor"
            control={control}
            defaultValue={titleMeta.ownersTitlePayor ?? ""}
            render={({ field }) => (
              <CustomSelect
                {...field}
                placeholder={titleMeta.ownersTitlePayor ?? "Select"}
                options={["Split", "Buyer", "Seller"]}
                onChange={(value) => {
                  field.onChange(value);

                  setFormData((prev) => {
                    const prevTitle = prev?.titleSettlementMeta ?? {};
                    let selectedAmount = "0.00";

                    switch (value) {
                      case "Split":
                        selectedAmount =
                          prevTitle.ownersTitleBuyer / 2 ?? "0.00";
                        break;
                      case "Buyer":
                        selectedAmount = prevTitle.ownersTitleBuyer ?? "0.00";
                        break;
                      case "Seller":
                        selectedAmount = "0.00";
                        break;
                      default:
                        selectedAmount = prevTitle.ownersTitleBuyer ?? "0.00";
                        break;
                    }

                    return {
                      ...prev,
                      titleSettlementMeta: {
                        ...prevTitle,
                        ownersTitlePayor: value,
                        ownersTitleAmount: selectedAmount,
                      },
                    };
                  });
                }}
              />
            )}
          />
        </div>
        <div className="calc-frm-fld form-group">
          <div className="calc-frm-field">
            <div className="calc-frm-fld form-group sign-of-amount no-m-space">
              <ControlledField
                name="titleSettlementMeta.ownersTitleAmount"
                control={control}
                InputComponent={FloatingNumber}
                placeholder="0.00"
                type="text"
                defaultValue={titleMeta.ownersTitleAmount ?? "0.00"}
                cleanNumber={cleanNumber}
                handleCurrencyBlur={handleCurrencyBlur}
                inputProps={{
                  disabled: EscrowDisabled.ownersPolicy,
                  className:
                    EscrowDisabled && EscrowDisabled.ownersPolicy === false
                      ? "premium-border"
                      : "",
                }}
                onFormatted={(fieldName, formatted) =>
                  onFormatted?.("ownersTitleAmount", formatted)
                }
              />
            </div>
            <div className="calc-frm-fld form-group no-m-space edit-container">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  openModal({
                    title: "Edit Owners Title Policy",
                    size: "smallmodel",
                    content: (
                      <EditEscrowModal
                        description="Owners Title Policy"
                        onClose={closeModal}
                        onContinue={(setting) =>
                          onContinue({ ownersPolicy: setting })
                        }
                      />
                    ),
                  });
                }}
              >
                <i className="icon-edit" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lender’s Title Policy */}
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group">
          <label className="lbl-section">Lenders Title Policy</label>
          <Controller
            name="titleSettlementMeta.lendersTitlePayor"
            control={control}
            defaultValue={titleMeta.lendersTitlePayor ?? ""}
            render={({ field }) => (
              <CustomSelect
                {...field}
                placeholder={titleMeta.lendersTitlePayor ?? "Select"}
                options={["Split", "Buyer", "Seller"]}
                onChange={(value) => {
                  field.onChange(value);

                  setFormData((prev) => {
                    const prevTitle = prev?.titleSettlementMeta ?? {};
                    let selectedAmount = "0.00";

                    switch (value) {
                      case "Split":
                        selectedAmount =
                          prevTitle.lendersTitleBuyer / 2 ?? "0.00";
                        break;
                      case "Buyer":
                        selectedAmount = prevTitle.lendersTitleBuyer ?? "0.00";
                        break;
                      case "Seller":
                        selectedAmount = "0.00";
                        break;
                      default:
                        selectedAmount = prevTitle.lendersTitleBuyer ?? "0.00";
                        break;
                    }

                    const updatedFormData = {
                      ...prev,
                      titleSettlementMeta: {
                        ...prevTitle,
                        lendersTitlePayor: value,
                        lendersTitleAmount: selectedAmount,
                      },
                    };

                    return applyWaSalesTaxClosingCosts(updatedFormData, {
                      escrowSettlementAmount:
                        updatedFormData?.titleSettlementMeta
                          ?.escrowSettlementAmount,
                      lendersTitleAmount: selectedAmount,
                    });
                  });
                }}
              />
            )}
          />
        </div>
        <div className="calc-frm-fld form-group">
          <div className="calc-frm-field">
            <div className="calc-frm-fld form-group no-m-space sign-of-amount">
              <ControlledField
                name="titleSettlementMeta.lendersTitleAmount"
                control={control}
                InputComponent={FloatingNumber}
                placeholder="0.00"
                type="text"
                defaultValue={titleMeta.lendersTitleAmount ?? "0.00"}
                cleanNumber={cleanNumber}
                handleCurrencyBlur={handleCurrencyBlur}
                inputProps={{
                  disabled: EscrowDisabled.lendersPolicy,
                  className:
                    EscrowDisabled && EscrowDisabled.lendersPolicy === false
                      ? "premium-border"
                      : "",
                }}
                onFormatted={(fieldName, formatted) => {
                  onFormatted?.("lendersTitleAmount", formatted);
                  setFormData((prev) => {
                    const prevTitle = prev?.titleSettlementMeta ?? {};
                    const updatedFormData = {
                      ...prev,
                      titleSettlementMeta: {
                        ...prevTitle,
                        lendersTitleAmount: formatted,
                      },
                    };

                    return applyWaSalesTaxClosingCosts(updatedFormData, {
                      escrowSettlementAmount: parseAmount(
                        updatedFormData?.titleSettlementMeta
                          ?.escrowSettlementAmount,
                      ),
                      lendersTitleAmount: parseAmount(formatted),
                    });
                  });
                }}
              />
            </div>
            <div className="calc-frm-fld form-group no-m-space edit-container">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  openModal({
                    title: "Edit Lenders Title Policy",
                    size: "smallmodel",
                    content: (
                      <EditEscrowModal
                        description="Lenders Title Policy"
                        onClose={closeModal}
                        onContinue={(setting) =>
                          onContinue({ lendersPolicy: setting })
                        }
                      />
                    ),
                  });
                }}
              >
                <i className="icon-edit" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* On-demand rows from the backend (closingCostOverrideService, mode:"title") — e.g. CA
          City/County Transfer Tax. Not part of the closingCostsMeta template; rendered the
          same way as Owners/Lenders Title Policy above. */}
      {(titleMeta.transferTaxRows || []).map((row, index) => (
        <div className="calc-frm-field" key={`${row.label}-${index}`}>
          <div className="calc-frm-fld form-group">
            <label className="lbl-section">{row.label}</label>
            <Controller
              name={`titleSettlementMeta.transferTaxRows.${index}.payor`}
              control={control}
              defaultValue={row.payor ?? ""}
              render={({ field }) => (
                <CustomSelect
                  {...field}
                  placeholder={row.payor || "Select"}
                  options={["Split", "Buyer", "Seller"]}
                  onChange={(value) => {
                    field.onChange(value);

                    setFormData((prev) => {
                      const prevTitle = prev?.titleSettlementMeta ?? {};
                      const rows = Array.isArray(prevTitle.transferTaxRows)
                        ? [...prevTitle.transferTaxRows]
                        : [];
                      const current = rows[index] ?? row;
                      const amount = computeTitleFeePayorAmount(
                        value,
                        current.baseAmount,
                      );
                      rows[index] = { ...current, payor: value, amount };

                      return {
                        ...prev,
                        titleSettlementMeta: {
                          ...prevTitle,
                          transferTaxRows: rows,
                        },
                      };
                    });
                  }}
                />
              )}
            />
          </div>
          <div className="calc-frm-fld form-group">
            <div className="calc-frm-field">
              <div className="calc-frm-fld form-group sign-of-amount no-m-space">
                <ControlledField
                  name={`titleSettlementMeta.transferTaxRows.${index}.amount`}
                  control={control}
                  InputComponent={FloatingNumber}
                  placeholder="0.00"
                  type="text"
                  defaultValue={row.amount ?? "0.00"}
                  cleanNumber={cleanNumber}
                  handleCurrencyBlur={handleCurrencyBlur}
                  onFormatted={(fieldName, formatted) => {
                    setFormData((prev) => {
                      const prevTitle = prev?.titleSettlementMeta ?? {};
                      const rows = Array.isArray(prevTitle.transferTaxRows)
                        ? [...prevTitle.transferTaxRows]
                        : [];
                      const current = rows[index] ?? row;
                      rows[index] = { ...current, amount: formatted };

                      return {
                        ...prev,
                        titleSettlementMeta: {
                          ...prevTitle,
                          transferTaxRows: rows,
                        },
                      };
                    });
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Discount */}
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group sign-of-percentage">
          <ControlledField
            name="titleSettlementMeta.discount"
            label="Discount %"
            control={control}
            InputComponent={FloatingNumber}
            placeholder="0.00"
            type="text"
            defaultValue={titleMeta.discount ?? "0.00"}
            cleanNumber={cleanNumber}
            handleCurrencyBlur={handleCurrencyBlur}
            onFormatted={(fieldName, formatted) => {
              return onFormatted("discount", formatted);
            }}
          />
        </div>

        <div className="calc-frm-fld form-group">
          <div className="calc-frm-field">
            <div className="calc-frm-fld form-group no-m-space sign-of-amount">
              <ControlledField
                name="titleSettlementMeta.discountAmount"
                control={control}
                InputComponent={FloatingNumber}
                placeholder="0.00"
                type="text"
                defaultValue={titleMeta.discountAmount ?? "0.00"}
                cleanNumber={cleanNumber}
                handleCurrencyBlur={handleCurrencyBlur}
                onFormatted={(fieldName, formatted) => {
                  return onFormatted("discountAmount", formatted);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
